import { translate } from '../i18n/translate';

/**
 * API response helpers.
 *
 * The backend wraps list endpoints inconsistently across modules — sometimes
 * `[items]` directly, sometimes `{ data: [items] }`, sometimes
 * `{ data: [items], meta: { total, page, totalPages } }`. These helpers
 * normalize that so call-sites don't repeat the same defensive code.
 */

/**
 * Extract a list of items from any of the supported response shapes.
 * Returns `[]` for null/undefined input — never throws.
 */
export function unwrapList(response) {
  if (Array.isArray(response)) return response;
  if (response == null) return [];
  if (Array.isArray(response.items)) return response.items;
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response?.data?.items)) return response.data.items;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
}

/**
 * Extract pagination metadata from a list response.
 * Returns sensible defaults if the shape doesn't match.
 */
export function unwrapMeta(response, fallback = {}) {
  const meta = response?.meta || response?.data?.meta || response?.pagination;
  return {
    total: meta?.total ?? fallback.total ?? 0,
    page: meta?.page ?? fallback.page ?? 1,
    limit: meta?.limit ?? fallback.limit ?? 20,
    totalPages: meta?.totalPages ?? fallback.totalPages ?? 1,
  };
}

/**
 * The sentence a failed request puts on screen. Same rule as utils/errorText (spec §5): the
 * backend speaks English, so its own text never reaches the page. What the API client worded
 * itself in the user's language (`messageKey`: offline, timed out, not found, server error, ...)
 * is shown; any other server or axios failure shows the caller's translated `fallback`. A plain
 * `Error` the client threw with its own (translated) sentence keeps that sentence.
 */
export function extractErrorMessage(err, fallback = translate('shell.errors.requestFailed')) {
  if (!err) return fallback;
  if (err.messageKey && err.message) return err.message;
  const fromServer = Boolean(err.response || err.isAxiosError) || !(err instanceof Error);
  if (fromServer) return fallback;
  return err.message || fallback;
}
